import { Link } from 'react-router-dom';
import { ArrowLeft, Scale, AlertTriangle, CheckCircle, Clock, MessageSquare } from 'lucide-react';

export default function DisputesPage() {
  return (
    <div className="tt-product min-h-screen bg-background">
      <nav className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-primary">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <Scale className="w-8 h-8 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Dispute Resolution</h1>
        </div>

        <div className="bg-card rounded-xl shadow-sm border border-border p-8 space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">When to Open a Dispute</h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                <p className="text-foreground">Item not received after expected delivery time</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                <p className="text-foreground">Item significantly different from description</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                <p className="text-foreground">Item damaged or defective</p>
              </div>
              <div className="flex items-start gap-3 p-3 bg-warning/10 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-warning mt-0.5" />
                <p className="text-foreground">Seller not responding or uncooperative</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">Dispute Process</h2>
            <div className="space-y-4">
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">1</div>
                <div>
                  <h3 className="font-semibold text-foreground">Open Dispute</h3>
                  <p className="text-sm text-muted-foreground">Click "Open Dispute" on your transaction page. Describe the issue clearly and upload any evidence (photos, screenshots, messages).</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">2</div>
                <div>
                  <h3 className="font-semibold text-foreground">Other Party Response</h3>
                  <p className="text-sm text-muted-foreground">The other party is asked to respond promptly with their side of the story and evidence.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">3</div>
                <div>
                  <h3 className="font-semibold text-foreground">TrustTrade Review</h3>
                  <p className="text-sm text-muted-foreground">Our AI analyses the evidence and gives our team a recommendation. A human admin always reviews the case and makes the final decision.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-8 h-8 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold">4</div>
                <div>
                  <h3 className="font-semibold text-foreground">Resolution</h3>
                  <p className="text-sm text-muted-foreground">Funds are released to the appropriate party based on our determination. Straightforward cases are typically resolved within 3-5 business days; complex cases needing more evidence can take up to 10 business days.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">Tips for Successful Resolution</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-sm text-muted-foreground">Provide clear photos of the item and any issues</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-sm text-muted-foreground">Screenshot all communication with the other party</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-sm text-muted-foreground">Keep tracking numbers and delivery receipts</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-success mt-0.5" />
                <p className="text-sm text-muted-foreground">Respond promptly to any requests for information</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mb-4">Need Help?</h2>
            <div className="flex items-center gap-3 p-4 bg-primary/10 rounded-lg">
              <MessageSquare className="w-6 h-6 text-primary" />
              <div>
                <p className="font-semibold text-foreground">Contact Support</p>
                <a href="mailto:trusttrade.register@gmail.com" className="text-primary hover:underline">
                  trusttrade.register@gmail.com
                </a>
              </div>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-muted-foreground text-sm">© 2026 TrustTrade South Africa. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
